/**
 * Team Management Modal
 * L0-CMD-2026-0120-011 - Company team management
 *
 * Allows company admins/owners to:
 * - View team members
 * - Invite new members by email
 * - Remove team members
 * - Cancel pending invitations
 */

import React, { useState, useEffect } from 'react';

interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  name: string;
  role: string;
  joined_at: string | null;
  is_pending: boolean;
  invited_at?: string;
  expires_at?: string;
}

interface TeamManagementModalProps {
  onClose: () => void;
}

const TeamManagementModal: React.FC<TeamManagementModalProps> = ({ onClose }) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite form state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('drafter');
  const [sending, setSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchTeam();
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const fetchTeam = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/companies/current/team', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch team');
      }

      const data = await response.json();
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch team');
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;

    setSending(true);
    setError(null);
    setInviteSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/companies/current/invite', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to send invite');
      }

      setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInvite(false);
      fetchTeam();

      // Clear success message after 3 seconds
      setTimeout(() => setInviteSuccess(null), 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const removeMember = async (memberId: string, memberName: string) => {
    if (!confirm(`Remove ${memberName} from the team?`)) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/companies/current/team/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to remove member');
      }

      fetchTeam();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2">
            <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z" />
          </svg>
        );
      case 'admin':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        );
      default:
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        );
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content team-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Team Members</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* Success Message */}
          {inviteSuccess && (
            <div className="success-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              {inviteSuccess}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="error-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              {error}
            </div>
          )}

          {/* Invite Button or Form */}
          {!showInvite ? (
            <button className="invite-btn" onClick={() => setShowInvite(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              Invite Team Member
            </button>
          ) : (
            <div className="invite-form">
              <div className="form-row">
                <input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  autoFocus
                />
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  <option value="drafter">Drafter</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={sendInvite}
                  disabled={sending || !inviteEmail.trim()}
                >
                  {sending ? 'Sending...' : 'Send Invite'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowInvite(false);
                    setInviteEmail('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Team List */}
          <div className="team-list">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <span>Loading team...</span>
              </div>
            ) : members.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <p>No team members yet</p>
                <span>Invite your first team member to get started</span>
              </div>
            ) : (
              members.map(member => (
                <div
                  key={member.id}
                  className={`team-member ${member.is_pending ? 'pending' : ''}`}
                >
                  <div className="member-avatar">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="member-info">
                    <div className="member-name">
                      {member.name}
                      {member.is_pending && (
                        <span className="pending-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                          </svg>
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="member-email">{member.email}</div>
                    {member.joined_at && (
                      <div className="member-date">Joined {formatDate(member.joined_at)}</div>
                    )}
                    {member.is_pending && member.expires_at && (
                      <div className="member-date">Expires {formatDate(member.expires_at)}</div>
                    )}
                  </div>
                  <div className="member-role">
                    {getRoleIcon(member.role)}
                    <span>{member.role}</span>
                  </div>
                  {member.role !== 'owner' && (
                    <button
                      className="remove-btn"
                      onClick={() => removeMember(member.id, member.name)}
                      title={member.is_pending ? 'Cancel invitation' : 'Remove from team'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          backdrop-filter: blur(4px);
        }

        .team-modal {
          background: var(--bg-secondary, #1e293b);
          border-radius: 12px;
          width: 100%;
          max-width: 560px;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border-color, #334155);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          color: var(--text-primary, #f8fafc);
        }

        .close-btn {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }

        .close-btn:hover {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .modal-body {
          padding: 20px 24px;
          overflow-y: auto;
          flex: 1;
        }

        .success-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
          color: #22c55e;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .error-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #f87171;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .invite-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px;
          background: var(--primary, #2563eb);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          margin-bottom: 20px;
          transition: opacity 0.2s;
        }

        .invite-btn:hover {
          opacity: 0.9;
        }

        .invite-form {
          background: var(--bg-tertiary, #334155);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .form-row {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
        }

        .form-row input {
          flex: 1;
          padding: 10px 12px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
        }

        .form-row input:focus {
          outline: none;
          border-color: var(--primary, #2563eb);
        }

        .form-row select {
          padding: 10px 12px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
          cursor: pointer;
        }

        .form-actions {
          display: flex;
          gap: 8px;
        }

        .btn-primary {
          padding: 10px 20px;
          background: var(--primary, #2563eb);
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: transparent;
          border: 1px solid var(--border-color, #475569);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
          cursor: pointer;
        }

        .team-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .loading-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: var(--text-secondary);
        }

        .loading-state .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--border-color, #334155);
          border-top-color: var(--primary, #2563eb);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state svg {
          margin-bottom: 16px;
          color: var(--text-muted, #64748b);
        }

        .empty-state p {
          margin: 0 0 4px 0;
          font-size: 15px;
          color: var(--text-primary);
        }

        .empty-state span {
          font-size: 13px;
        }

        .team-member {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--bg-tertiary, #334155);
          border-radius: 8px;
          transition: background 0.2s;
        }

        .team-member:hover {
          background: #3d4a5e;
        }

        .team-member.pending {
          opacity: 0.7;
        }

        .member-avatar {
          width: 40px;
          height: 40px;
          background: var(--primary, #2563eb);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 16px;
          flex-shrink: 0;
        }

        .member-info {
          flex: 1;
          min-width: 0;
        }

        .member-name {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .pending-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          background: rgba(234, 179, 8, 0.1);
          border-radius: 4px;
          font-size: 11px;
          color: #eab308;
          font-weight: 500;
        }

        .member-email {
          font-size: 13px;
          color: var(--text-secondary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .member-date {
          font-size: 11px;
          color: var(--text-muted, #64748b);
          margin-top: 2px;
        }

        .member-role {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: var(--bg-secondary);
          border-radius: 4px;
          font-size: 12px;
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        .remove-btn {
          background: none;
          border: none;
          color: var(--text-muted, #64748b);
          cursor: pointer;
          padding: 6px;
          border-radius: 4px;
          opacity: 0;
          transition: all 0.2s;
        }

        .team-member:hover .remove-btn {
          opacity: 1;
        }

        .remove-btn:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
      `}</style>
    </div>
  );
};

export default TeamManagementModal;
